import type { SerializedLexicalNode } from 'lexical';
import type React from 'react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Button } from '../../../components/ui/button.tsx';
import { useVoiceRecording } from '../../../lib/contexts/voice-recording-context.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { cn } from '../../../lib/utils.ts';
import type { RichTextInputProps } from './rich-text-input.tsx';
import { RichTextInput, type RichTextValue } from './rich-text-input.tsx';

type RichTextForm = {
    allowEmpty?: boolean;
    onSubmit: (content: RichTextValue) => void;
    onCancel?: () => void;
    isSubmitting?: boolean;
    initialValue?: RichTextValue | null;
    submitTitle?: ReactNode;
    className?: string;
    style?: React.CSSProperties;
} & Omit<RichTextInputProps, 'value' | 'onChange'>;

export const RichTextForm = ({
    allowEmpty = false,
    submitTitle,
    onSubmit,
    onCancel,
    placeholder,
    isSubmitting = false,
    initialValue = null,
    className,
    style,
    variant = 'default',
    ...props
}: RichTextForm): ReactNode => {
    const { t } = useLocale('common');
    const { currentSessionId } = useVoiceRecording();
    const [content, setContent] = useState<RichTextValue | null>(initialValue);

    const handleSubmit = (): void => {
        if (content && !allowEmpty) {
            onSubmit(content);
            setContent(null);
        }
    };

    const handleCancel = (): void => {
        setContent(null);
        onCancel?.();
    };

    // Recursively checks nested structures (e.g. list → listitem → text)
    const nodeHasText = (node: SerializedLexicalNode): boolean => {
        if ('text' in node && typeof node.text === 'string' && node.text.trim()) {
            return true;
        }
        if ('children' in node && Array.isArray(node.children)) {
            return node.children.some(nodeHasText);
        }
        return false;
    };
    const hasValidContent = content?.root.children.some(nodeHasText);

    const canSubmit = (hasValidContent ?? allowEmpty) && !isSubmitting && !currentSessionId;

    const isInline = variant === 'inline';

    return (
        <div className={cn('group flex flex-col gap-3', className)} style={style}>
            <div className={cn(!isInline && 'rounded-lg bg-white')}>
                <RichTextInput
                    placeholder={placeholder}
                    value={content ?? undefined}
                    onChange={setContent}
                    variant={variant}
                    {...props}
                />
            </div>
            <div
                className={cn(
                    'justify-end items-center',
                    // In inline mode the action row is hidden until the user focuses the editor
                    // or has entered content — matching the previous "placeholder → form" flow
                    // but driven by CSS focus-within instead of JS state.
                    isInline && !hasValidContent ? 'hidden group-focus-within:flex' : 'flex',
                )}
            >
                <div className="flex gap-2">
                    <Button variant="subtle" size="sm" onClick={handleCancel} disabled={isSubmitting}>
                        {t('form__cancel')}
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        isLoading={isSubmitting}
                    >
                        {submitTitle ?? t('form__submit')}
                    </Button>
                </div>
            </div>
        </div>
    );
};
